import { HttpErrorResponse } from '@angular/common/http';
import type { ErrorHandler } from '@angular/core';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { isStandardError } from '@shared/errors';
import { PostHogService } from '../posthog';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  readonly #toast = inject(HotToastService);
  readonly #translator = inject(TranslateService);
  readonly #posthog = inject(PostHogService);

  handleError(error: any): void {
    console.error(error);
    this.#captureForPostHog(error);
    try {
      this.#displayError(error);
    } catch (handlerError) {}
  }

  #captureForPostHog(error: any): void {
    const posthog = this.#posthog.getPostHog();
    if (!posthog) return;

    const extracted = this.#extractError(error);
    if (!extracted) return;

    posthog.captureException(extracted.error, extracted.context);
  }

  // PostHog renders "No stacktrace available" when the captured value lacks
  // a .stack string. HttpErrorResponse, plain-object throws, and rejected
  // promises with non-Error reasons all fall into that bucket. Normalize to
  // a real Error here so PostHog can symbolicate against uploaded maps.
  #extractError(
    raw: any,
  ): { error: Error; context?: Record<string, unknown> } | null {
    const unwrapped =
      raw?.error &&
      !(raw instanceof HttpErrorResponse) &&
      !(raw instanceof Error)
        ? raw.error
        : raw;

    if (unwrapped instanceof HttpErrorResponse) {
      const synthetic = new Error(
        `HTTP ${unwrapped.status} ${unwrapped.statusText ?? ''} ${unwrapped.url ?? ''}`.trim(),
      );
      synthetic.name = 'HttpErrorResponse';
      return {
        error: synthetic,
        context: {
          status: unwrapped.status,
          statusText: unwrapped.statusText,
          url: unwrapped.url,
          responseBody: unwrapped.error,
        },
      };
    }

    if (unwrapped instanceof Error) {
      return { error: unwrapped };
    }

    if (unwrapped == null) return null;

    const message =
      typeof unwrapped === 'string'
        ? unwrapped
        : typeof unwrapped === 'object' &&
            typeof (unwrapped as { message?: unknown }).message === 'string'
          ? (unwrapped as { message: string }).message
          : JSON.stringify(unwrapped);
    return { error: new Error(message) };
  }

  #displayError(error: any): void {
    if (error instanceof HttpErrorResponse) {
      this.#handleHttpError(error);
    } else if (error?.error) {
      this.#displayError(error.error);
    } else if (error?.message) {
      this.#handleGenericError(error);
    } else {
      this.#handleUnknownError(error);
    }
  }

  #handleGenericError(error: any): void {
    const message = error.message;

    // Check for specific error patterns in message
    if (message.toLowerCase().includes('chunk')) {
      this.#toast.info(
        this.#translator.instant('enum.clientError.chunkLoadError'),
        {
          duration: 6000,
        },
      );
    } else if (message.toLowerCase().includes('script error')) {
      this.#toast.warning(
        this.#translator.instant('enum.clientError.scriptError'),
        {
          duration: 4000,
        },
      );
    } else {
      this.#toast.error(this.#truncateMessage(message), {
        duration: 4000,
      });
    }
  }

  #handleUnknownError(error: any): void {
    this.#toast.error(
      this.#translator.instant('enum.clientError.unexpectedError'),
      {
        duration: 4000,
      },
    );
  }

  #handleHttpError(error: HttpErrorResponse): void {
    if (!isStandardError(error.error)) {
      this.#toast.error(this.#translator.instant('enum.error.e00179'), {
        duration: 4000,
        id: 'internal-server-error',
      });
    }
  }

  #truncateMessage(message: string): string {
    // Toast messages should be concise but readable
    const maxLength = 85;
    if (message.length <= maxLength) {
      return message;
    }
    return `${message.substring(0, maxLength)}...`;
  }
}
